import React from 'react'
import clsx from 'clsx'
import { Logger } from 'wdc-cube'
import { makeStyles } from 'tss-react/mui'

import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import DownloadIcon from '@mui/icons-material/Download'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff'
import FilterListOffIcon from '@mui/icons-material/FilterListOff'
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Tooltip from '@mui/material/Tooltip'
import {
    GridColDef,
    GridColumnMenu,
    GridColumnMenuProps,
    GridRenderCellParams,
    GridRowParams,
    GridSortModel,
    GridValidRowModel
} from '@mui/x-data-grid'

import * as fmtUtils from 'src/utils/formatter-utils'
import { formatNumber } from 'src/utils/formatter-utils'
import {
    CardIconButton,
    LifesHubCard,
    LifesHubCardBody,
    LifesHubCardHeader,
    LifesHubEmptyState,
    WorkingBar
} from 'src/utils/views'
import { FCClassContext, LocaleProvider, classToFComponent } from 'src/utils/views'

import {
    ProfessionalCardScope,
    type ProfessionalExistsFilterField,
    type ProfessionalExistsFilterMode,
    type ProfessionalRow
} from '../ta_scopes'
import { ProfessionalSortModel } from '../ta_types'
import { ROWS_PER_PAGE_OPTIONS, TaDataGrid, TaPagination, TaSkeleton } from 'src/utils/views/modules/the-acting'

declare module '@mui/x-data-grid' {
    interface ColumnMenuPropsOverrides {
        scope: ProfessionalCardScope
    }
}

const LOG = Logger.get('TA-VIEW-PROF-CARD')

const DETAIL_COLUMN = '__ficha'

const useStyles = makeStyles()({
    view: {
        position: 'relative',
        alignSelf: 'stretch',
        flexGrow: 1,
        minHeight: 0
    },
    table: {
        alignSelf: 'stretch',
        flex: 1,
        flexBasis: 240,
        minHeight: 0,

        [`& .MuiDataGrid-columnHeader[data-field="${DETAIL_COLUMN}"],
          & .MuiDataGrid-cell[data-field="${DETAIL_COLUMN}"]`]: {
            position: 'sticky',
            left: 0,
            zIndex: 2,
            backgroundColor: 'var(--surface)'
        },
        [`& .MuiDataGrid-columnHeader[data-field="${DETAIL_COLUMN}"]`]: {
            zIndex: 3
        }
    }
})

export type ProfessionalCardViewProps = {
    className?: string
    scope: ProfessionalCardScope
}

class ProfessionalCardViewClass implements FCClassContext<ProfessionalCardViewProps> {
    // :: Fields

    scope!: ProfessionalCardScope

    // :: Emissors

    readonly emitExpandClick = () => this.scope.onExpandClick().catch(LOG.caught)
    readonly emitShrunkClick = () => this.scope.onShrunkClick().catch(LOG.caught)
    readonly emitDownloadClick = () => this.scope.onDownloadClick().catch(LOG.caught)

    readonly emitRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const pageSize = parseInt(e.target.value, 10)
        this.scope.onPageSizeChange(pageSize).catch(LOG.caught)
    }

    readonly emitChangePage = (__e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
        this.scope.onPageChange(newPage).catch(LOG.caught)
    }

    readonly emitRowClick = (params: GridRowParams<ProfessionalRow>) => {
        this.scope.onRowClick(params.row).catch(LOG.caught)
    }

    readonly emitDetailClick = (row: ProfessionalRow) => {
        this.scope.onDetailClick(row).catch(LOG.caught)
    }

    readonly columns = static_buildColumns(this.emitDetailClick)

    readonly emitSortModelChange = (gridSortModel: GridSortModel) => {
        const data: ProfessionalSortModel[] = []
        for (const item of gridSortModel) {
            data.push({
                field: item.field as ProfessionalSortModel['field'],
                sort: item.sort ?? 'asc'
            })
        }
        this.scope.onSortModelChange(data).catch(LOG.caught)
    }

    // :: Methods

    onSyncState({ scope }: ProfessionalCardViewProps) {
        this.scope = scope
    }

    render({ className, scope }: ProfessionalCardViewProps) {
        const classes = useStyles().classes

        const empty = scope.rows.length === 0

        return (
            <LifesHubCard className={clsx(classes.view, className)} elevation={0}>
                <LifesHubCardHeader
                    title="Profissionais médicos"
                    count={scope.total > 0 ? formatProfessionalCount(scope.total) : undefined}
                    actions={
                        <>
                            {scope.downloadEnabled && (
                                <Tooltip title="Exportar profissionais">
                                    <CardIconButton
                                        aria-label="Exportar profissionais"
                                        onClick={this.emitDownloadClick}
                                    >
                                        <DownloadIcon />
                                    </CardIconButton>
                                </Tooltip>
                            )}
                            <Tooltip title={scope.expanded ? 'Restaurar card' : 'Maximizar card'}>
                                <CardIconButton
                                    aria-label={scope.expanded ? 'Restaurar card' : 'Maximizar card'}
                                    onClick={scope.expanded ? this.emitShrunkClick : this.emitExpandClick}
                                >
                                    {scope.expanded && <CloseFullscreenRoundedIcon />}
                                    {!scope.expanded && <LaunchRoundedIcon />}
                                </CardIconButton>
                            </Tooltip>
                        </>
                    }
                />
                <LifesHubCardBody>
                    {empty && scope.working && <TaSkeleton aria-hidden="true" />}
                    {empty && !scope.working && (
                        <LifesHubEmptyState
                            title="Nenhum profissional no recorte"
                            text="Busque na barra superior ou remova um filtro do painel lateral para trazer os profissionais médicos vinculados."
                        />
                    )}
                    {!empty && (
                        <TaDataGrid
                            className={classes.table}
                            rows={scope.rows}
                            columns={this.columns}
                            sortingMode="server"
                            paginationMode="client"
                            getRowId={static_getRowId}
                            disableVirtualization
                            hideFooter
                            hideFooterPagination
                            onRowClick={this.emitRowClick}
                            onSortModelChange={this.emitSortModelChange}
                            disableColumnFilter={true}
                            slots={{ columnMenu: ProfessionalColumnMenu }}
                            slotProps={{ columnMenu: { scope } }}
                            initialState={{
                                sorting: {
                                    sortModel: scope.sortBy
                                }
                            }}
                        />
                    )}
                    {scope.working && <WorkingBar />}
                    {scope.total > scope.pageSize && (
                        <TaPagination
                            component="div"
                            count={scope.total}
                            page={scope.page}
                            onPageChange={this.emitChangePage}
                            rowsPerPage={scope.pageSize}
                            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                            onRowsPerPageChange={this.emitRowsPerPageChange}
                        />
                    )}
                </LifesHubCardBody>
            </LifesHubCard>
        )
    }
}
export const ProfessionalCardView = classToFComponent(ProfessionalCardViewClass, React)
export default ProfessionalCardView

function static_getRowId(row: GridValidRowModel) {
    return (row as ProfessionalRow).id
}

function formatProfessionalCount(total: number) {
    return total === 1 ? '1 profissional encontrado' : `${formatNumber(total)} profissionais encontrados`
}

function static_buildColumns(onDetailClick: (row: ProfessionalRow) => void): GridColDef[] {
    const fns = LocaleProvider.get().fns
    return [
        {
            field: DETAIL_COLUMN,
            headerName: 'Ficha',
            width: 62,
            sortable: false,
            filterable: false,
            hideable: false,
            disableColumnMenu: true,
            disableReorder: true,
            align: 'center',
            renderCell: (params: GridRenderCellParams) => (
                <CardIconButton
                    aria-label={`Ver ficha de ${params.row.nome}`}
                    onClick={(event) => {
                        event.stopPropagation()
                        onDetailClick(params.row as ProfessionalRow)
                    }}
                    sx={{
                        width: 28,
                        height: 28,
                        opacity: 0,
                        transition: 'opacity 120ms',
                        '& .MuiSvgIcon-root': { fontSize: 18 },
                        '.MuiDataGrid-row:hover &': { opacity: 1 },
                        '&:focus-visible': { opacity: 1 },
                        '@media (hover: none)': { opacity: 1 }
                    }}
                >
                    <InfoOutlinedIcon />
                </CardIconButton>
            )
        },
        {
            field: 'nome',
            headerName: 'Nome do profissional',
            width: 250,
            renderCell: (params: GridRenderCellParams) => <CellTitle value={params.value} />
        },
        {
            field: 'crm',
            headerName: 'CRM',
            width: 100,
            valueFormatter: (value) => fmtUtils.formatCRM(value)
        },
        {
            field: 'dataInscricao',
            headerName: 'Data de inscrição',
            type: 'date',
            width: 110,
            valueFormatter: (value) => fns.formatDate(value)
        },
        {
            field: 'situacao',
            headerName: 'Situação',
            width: 80
        },
        {
            field: 'especialidade',
            headerName: 'Especialidades / área de atuação',
            width: 160
        },
        {
            field: 'inscricao',
            headerName: 'Inscrição',
            width: 80
        },
        {
            field: 'dataPrimeiraInscricaoUF',
            headerName: 'Data da primeira inscrição na UF',
            type: 'date',
            width: 110,
            valueFormatter: (value) => fns.formatDate(value)
        },
        {
            field: 'outrosCRMs',
            headerName: 'Outros CRMs',
            width: 160
        },
        {
            field: 'instituicaoFormacao',
            headerName: 'Instituição de formação',
            width: 240,
            sortable: false
        },
        {
            field: 'anoFormatura',
            headerName: 'Ano de formatura',
            type: 'number',
            width: 110,
            valueFormatter: (value) => (value ? `${value}` : '')
        },
        {
            field: 'qualificacaoSocio',
            headerName: 'Qualificação do sócio',
            width: 180,
            flex: 1
        },
        {
            field: 'endereco',
            headerName: 'Endereço',
            width: 160,
            flex: 1
        },
        {
            field: 'telefone',
            headerName: 'Telefone',
            width: 160,
            valueGetter: (_value, row) => fmtUtils.formatTelefone(row.telefone)
        },
        {
            field: 'celular',
            headerName: 'Celular',
            width: 160,
            valueGetter: (_value, row) => fmtUtils.formatTelefone(row.celular)
        },
        {
            field: 'email',
            headerName: 'E-mail',
            width: 160,
            flex: 1
        }
    ]
}

function CellTitle({ value }: { value?: unknown }) {
    const label = value == null ? '' : String(value)
    return (
        <span style={{ fontWeight: 750, overflow: 'hidden', textOverflow: 'ellipsis' }} title={label}>
            {label}
        </span>
    )
}

// ---------------------------------------------------------------------------
// Custom column menu: "Filtrar somente com / sem valores" e "Limpar filtros"
// ---------------------------------------------------------------------------

const EXISTS_FILTER_FIELDS = new Set<ProfessionalExistsFilterField>(['endereco', 'telefone', 'celular', 'email'])

type ColumnMenuExtraProps = {
    scope: ProfessionalCardScope
}

type ExistsItemProps = {
    onClick: (event: React.MouseEvent<HTMLElement>) => void
    disabled?: boolean
    label: string
    icon: React.ReactNode
    onAction: () => void
}

function ExistsFilterMenuItem(props: ExistsItemProps) {
    const { onClick, disabled, label, icon, onAction } = props
    return (
        <MenuItem
            disabled={disabled}
            onClick={(event) => {
                onAction()
                onClick(event)
            }}
        >
            <ListItemIcon>{icon}</ListItemIcon>
            <ListItemText>{label}</ListItemText>
        </MenuItem>
    )
}

function ProfessionalColumnMenu(props: GridColumnMenuProps & ColumnMenuExtraProps) {
    const { scope, ...gridProps } = props
    const field = gridProps.colDef.field as ProfessionalExistsFilterField

    if (!EXISTS_FILTER_FIELDS.has(field)) {
        return <GridColumnMenu {...gridProps} />
    }

    const existsFilters = scope.existsFilters ?? {}
    const currentMode = existsFilters[field]
    const hasAnyFilter = Object.keys(existsFilters).length > 0

    const setMode = (mode: ProfessionalExistsFilterMode) => {
        scope.onExistsFilterChange({ field, mode }).catch(LOG.caught)
    }
    const clearAll = () => {
        scope.onClearExistsFilters().catch(LOG.caught)
    }

    return (
        <GridColumnMenu
            {...gridProps}
            slots={{
                columnMenuExistsWithItem: ExistsFilterMenuItem,
                columnMenuExistsWithoutItem: ExistsFilterMenuItem,
                columnMenuClearExistsItem: ExistsFilterMenuItem
            }}
            slotProps={{
                columnMenuExistsWithItem: {
                    displayOrder: 60,
                    label: 'Filtrar somente com valores',
                    icon: <FilterAltIcon fontSize="small" />,
                    disabled: currentMode === 'with',
                    onAction: () => setMode('with')
                },
                columnMenuExistsWithoutItem: {
                    displayOrder: 61,
                    label: 'Filtrar somente sem valores',
                    icon: <FilterAltOffIcon fontSize="small" />,
                    disabled: currentMode === 'without',
                    onAction: () => setMode('without')
                },
                columnMenuClearExistsItem: {
                    displayOrder: 62,
                    label: 'Limpar filtros das colunas',
                    icon: <FilterListOffIcon fontSize="small" />,
                    disabled: !hasAnyFilter,
                    onAction: clearAll
                }
            }}
        />
    )
}
